import { createContext, ReactNode, useEffect, useRef, useState } from "react";
import './ParticipantSearch.css'
import useElementVisible from "../../hooks/useElementVisible";
import Loader from "../UI/Loader";
import Client from "../../models/serviceModels/Client";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import FieldSearchPanel, { ParticipantFilter } from "./FieldSearchPanel";
import { searchPrograms } from "../../services/programsService";
import useSortableList from "../../hooks/useSortableList";
import Search, { SearchEvent } from "../UI/Search";
import { executeSearch, getDefaultSearchFilters, getSearchFields } from "../../services/searchService";
import SearchFieldReference from "../../models/serviceModels/SearchFieldReference";
import { CLIENT_SEARCH } from "../../constants/searchType";
import DefaultSearchFilter from "../../models/serviceModels/DeafultSearchFilter";
import { assignedUserEnabled, householdIdentifiersEnabled, manageProgramsV2Enabled } from "../../utils/features";

interface ClientResultsContextValue {
    clients: Client[] | undefined;
    refresh: () => void;
    isFetching?: boolean;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    onSort?: (key: string) => void;
}

const ClientResultsContext = createContext<ClientResultsContextValue | undefined>(undefined);

interface ParticipantSearchProps {
    children: ReactNode;
}

const TAKE = 50;

const LegacyParticipantSearch = ({ children }: ParticipantSearchProps) => {
    const [searching, setSearching] = useState(false);
    const [paging, setPaging] = useState(false);
    const [skip, setSkip] = useState(0);
    const [clients, setClients] = useState<Client[]>();
    const [totalCount, setTotalCount] = useState(0);
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const [searchFields, setSearchFields] = useState<SearchFieldReference[]>([]);
    const [searchFilters, setSearchFilters] = useState<DefaultSearchFilter[]>([]);
    const [apiError, setApiError] = useState<any>();
    const loaded = useRef(false);
    const [searchEvent, setSearchEvent] = useState<SearchEvent>();

    const search = (event: SearchEvent, nextSkip: number) => executeSearch<Client>({
        fields: event.fields?.map(field => ({
            id: field.fieldId,
            filterTypeId: field.filterTypeId,
            value: field.value
        })) || [],
        disabledFilters: event.disabledFilters,
        skip: nextSkip,
        take: TAKE,
        searchTypeId: CLIENT_SEARCH,
        query: event.query
    });

    const handleOnSearch = (event: SearchEvent) => {
        setSearching(true);
        setSkip(0);
        setSearchEvent(event);
        setApiError(undefined);

        search(event, 0)
            .then(result => {
                setClients(result.data);
                setTotalCount(result.totalCount);
            })
            .catch(error => {
                console.error(error);
                setApiError(error);
            })
            .finally(() => setSearching(false));
    };

    const handleRefresh = () => {
        if (!searchEvent) return;
        setSearching(true);
        setApiError(undefined);

        search(searchEvent, 0)
            .then(result => {
                setClients(result.data);
                setTotalCount(result.totalCount);
            })
            .catch(error => {
                console.error(error);
                setApiError(error);
            })
            .finally(() => setSearching(false));
    };

    useEffect(() => {
        if (!loaded.current || skip === 0 || !searchEvent) return;

        setApiError(undefined);
        setPaging(true);
        search(searchEvent, skip)
            .then(result => {
                setClients(current => current ? [...current, ...result.data] : [...result.data]);
                setTotalCount(result.totalCount);
            })
            .catch(error => {
                console.error(error);
                setApiError(error);
            })
            .finally(() => setPaging(false));
    }, [skip, searchEvent]);

    useEffect(() => {
        if (visible && clients && clients.length < totalCount && !paging) {
            setSkip(current => current + TAKE);
        }
    }, [visible, clients, totalCount, paging]);

    useEffect(() => {
        loaded.current = true;
    }, []);

    useEffect(() => {
        Promise.all([getSearchFields(CLIENT_SEARCH), getDefaultSearchFilters(CLIENT_SEARCH)])
            .then(([fieldsResult, filtersResult]) => {
                setSearchFilters(filtersResult);
                let availableFields = fieldsResult;

                if (!assignedUserEnabled) {
                    availableFields = availableFields.filter(field => field.fieldName !== 'Assigned User' && field.fieldName !== 'Assigned');
                }

                if (!householdIdentifiersEnabled) {
                    availableFields = availableFields.filter(field => field.fieldName !== 'Household ID');
                }

                setSearchFields(availableFields);
            })
            .catch(error => {
                console.error(error);
                setApiError(error);
            });
    }, []);

    return (
        <>
            <ApiErrorMessages error={apiError} />
            {searchFields.length !== 0 && (
                <Search
                    id="participant-search"
                    fields={searchFields}
                    defaultFilters={searchFilters}
                    searching={searching}
                    onSearch={handleOnSearch}
                />
            )}

            <ClientResultsContext.Provider value={{ clients, refresh: handleRefresh }}>
                {children}
            </ClientResultsContext.Provider>

            {paging && <Loader />}
            {clients && clients.length < totalCount && (
                <div ref={reference} className="padding-5 margin-bottom-5"></div>
            )}
        </>
    );
};

const SortableParticipantSearch = ({ children }: ParticipantSearchProps) => {
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const [apiError, setApiError] = useState<any>();
    const [programOptions, setProgramOptions] = useState<{ text: string; value: string }[]>([]);
    const [filter, setFilter] = useState<Record<string, unknown>>({});
    const [hasSearched, setHasSearched] = useState(false);

    const { data, totalCount, isFetching, sortBy, sortDirection, onSort, triggerSearch, fetchNextPage, hasNextPage } =
        useSortableList<Client>({
            entity: 'clients',
            filter,
            take: TAKE,
            defaultSortBy: 'fullName',
            enabled: hasSearched,
        });

    useEffect(() => {
        searchPrograms({ take: 500 })
            .then(result => setProgramOptions(result.data.map(program => ({ text: program.name, value: program.id }))))
            .catch(error => {
                console.error(error);
                setApiError(error);
            });
    }, []);

    useEffect(() => {
        if (visible && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    }, [visible, hasNextPage, isFetching, fetchNextPage]);

    const handleSearch = (incoming: ParticipantFilter) => {
        setApiError(undefined);
        setHasSearched(true);
        setFilter({
            query: incoming.query,
            fields: incoming.fields,
            excludeInactive: incoming.excludeInactive,
            assignedToMe: incoming.assignedToMe,
        });
        triggerSearch();
    };

    const clients = hasSearched ? data : undefined;

    return (
        <>
            <ApiErrorMessages error={apiError} />
            <FieldSearchPanel
                searching={isFetching}
                hasSearched={hasSearched}
                programOptions={programOptions}
                onSearch={handleSearch}
            />

            <ClientResultsContext.Provider value={{ clients, refresh: triggerSearch, isFetching, sortBy, sortDirection, onSort }}>
                {children}
            </ClientResultsContext.Provider>

            {isFetching && <Loader />}
            {hasSearched && data.length < totalCount && (
                <div ref={reference} className="padding-5 margin-bottom-5"></div>
            )}
        </>
    );
};

function ParticipantSearch(props: ParticipantSearchProps) {
    return manageProgramsV2Enabled
        ? <SortableParticipantSearch {...props} />
        : <LegacyParticipantSearch {...props} />;
}

export { ClientResultsContext, ParticipantSearch };
